import type { IntlShape } from 'react-intl'
import { ESIM_IMAGE_ID } from 'loyalty/constants/loyaltyEsimConstants'

export interface LoyaltyLandingPageBenefitDetails {
  title: React.ReactNode
  description: React.ReactNode
  image: App.Image
  benefitCode: App.LoyaltyBenefitType
  enabled: boolean
}

export type LoyaltyLandingPageBenefitCopy = Record<
  App.LoyaltyBenefitType,
  LoyaltyLandingPageBenefitDetails | undefined
>

export const getLoyaltyLandingPageBenefitCopy = (
  enabledBenefits: App.LoyaltyRegionEnabledBenefits,
  intl: IntlShape,
): LoyaltyLandingPageBenefitCopy => {
  return {
    hotel_upgrade: {
      title: intl.formatMessage({
        id: 'Rgs6todiX7dlM68BJRtP',
        defaultMessage: 'Upgrade your stay',
        description: 'Loyalty benefit title for hotel room upgrade benefit',
      }),
      description: intl.formatMessage({
        id: '9_k8yA7VkNjkixUbaaNc',
        defaultMessage: 'Enjoy complimentary room and package upgrades on select offers. Your upgrade is guaranteed as soon as you book, ensuring a seamless check-in with no surprises.',
        description: 'Loyalty benefit description for hotel room upgrade benefit',
      }),
      image: {
        id: 'wfzka1j5ru5d5e7hc9u',
        title: intl.formatMessage({
          id: 'JYNb9BNUnBDsgmkaaFxj',
          defaultMessage: 'Free room and package upgrades',
          description: 'Alt text for loyalty benefit image showing hotel room upgrade perk',
        }),
      },
      benefitCode: 'hotel_upgrade',
      enabled: enabledBenefits.hotel_upgrade,
    },
    airport_transfer: {
      title: intl.formatMessage({
        id: 'BpCwwAEHJnI5MXUTyuEH',
        defaultMessage: 'Travel in style with private chauffeur transfers',
        description: 'Loyalty benefit title for airport transfer benefit',
      }),
      description: intl.formatMessage({
        id: 'mWaHTMKMWR0BTqzT33He',
        defaultMessage: 'Start and end your journey smoothly with free private airport transfers in select destinations. No stress, just seamless service.',
        description: 'Loyalty benefit description for airport transfer benefit',
      }),
      image: {
        id: '0qt5tc9nvf1qd3f0t5fe',
        title: intl.formatMessage({
          id: 'BOfwgT4Fge1JO7WiU2W6',
          defaultMessage: 'Free airport chauffeur service',
          description: 'Alt text for loyalty benefit image showing airport chauffeur transfer perk',
        }),
      },
      benefitCode: 'airport_transfer',
      enabled: enabledBenefits.airport_transfer,
    },
    lounge_pass: {
      title: intl.formatMessage({
        id: 'iPDXaTjCyZukSdctAlBg',
        defaultMessage: 'Lounge in comfort before your flight',
        description: 'Loyalty benefit title for airport lounge pass benefit',
      }),
      description: intl.formatMessage({
        id: 'W2OMfseVTxY1nXhHb3xy',
        defaultMessage: 'Unwind with a drink, a bite to eat, and a quiet space to relax, the perfect start to your Luxury Escape.',
        description: 'Loyalty benefit description for airport lounge pass benefit',
      }),
      image: {
        id: 'rq13k5lzgr8oxoz3a2',
        title: intl.formatMessage({
          id: 'SzuxQfetKo4ZY7H01OzB',
          defaultMessage: 'Free airport lounge passes',
          description: 'Alt text for loyalty benefit image showing airport lounge access perk',
        }),
      },
      benefitCode: 'lounge_pass',
      enabled: enabledBenefits.lounge_pass,
    },
    phone_support: {
      title: intl.formatMessage({
        id: 'sl3fecu8rn0aropw1CoT',
        defaultMessage: 'Priority support',
        description: 'Loyalty benefit title for priority phone support benefit',
      }),
      description: intl.formatMessage({
        id: 'nmib7siN7ni1ZNlItVBU',
        defaultMessage: 'Skip the queue with 24/7 access to our dedicated support team, always here to help, whenever you need us.',
        description: 'Loyalty benefit description for priority phone support benefit',
      }),
      image: {
        id: 'kf3r6fahlubu2yb1htkgb',
        title: intl.formatMessage({
          id: 'l0WRJtR4V_bbpudqG16c',
          defaultMessage: 'Faster, priority support',
          description: 'Alt text for loyalty benefit image showing priority customer support perk',
        }),
      },
      benefitCode: 'phone_support',
      enabled: enabledBenefits.phone_support,
    },
    travelshoot_credit: {
      title: intl.formatMessage({
        id: '8lIkelcSiizsK8U4NIRd',
        defaultMessage: 'Discounted photoshoots',
        description: 'Loyalty benefit title for Travelshoot photo credit benefit',
      }),
      description: intl.formatMessage({
        id: 'Nv8nJ5wZJD8XRvyhYdC7',
        defaultMessage: 'Capture your escape in style with exclusive discounts on professional Travelshoot photo sessions at your destination.',
        description: 'Loyalty benefit description for Travelshoot photo credit benefit',
      }),
      image: {
        id: 'r1z816jjifeqb5hwk50o',
        title: intl.formatMessage({
          id: 'KaV7NcjcmDRCpgxEzgKb',
          defaultMessage: 'Pro photos on your trip',
          description: 'Alt text for loyalty benefit image showing professional photography perk',
        }),
      },
      benefitCode: 'travelshoot_credit',
      enabled: enabledBenefits.travelshoot_credit,
    },
    tour_exp_credit: {
      title: intl.formatMessage({
        id: 'eqnhoGepP4kuO9kJeI5Z',
        defaultMessage: 'Extras on your tour',
        description: 'Loyalty benefit title for tour experience credit benefit',
      }),
      description: intl.formatMessage({
        id: '64RQKiTDFk0mz_QekX5O',
        defaultMessage: 'Unlock credits for exclusive add-ons, from relaxing massages to local food experiences.',
        description: 'Loyalty benefit description for tour experience credit benefit',
      }),
      image: {
        id: '69vg83q7obl8xw4x1ac',
        title: intl.formatMessage({
          id: '_TnDMpmHMzhzfYrPlQXm',
          defaultMessage: 'Extras on your tour',
          description: 'Alt text for loyalty benefit image showing tour experience credit perk',
        }),
      },
      benefitCode: 'tour_exp_credit',
      enabled: enabledBenefits.tour_exp_credit,
    },
    discounted_insurance: {
      title: intl.formatMessage({
        id: 'ekNGW6zLfI_zBhZI1MFU',
        defaultMessage: 'Discounted travel insurance',
        description: 'Loyalty benefit title for discounted insurance benefit',
      }),
      description: intl.formatMessage({
        id: '7f1DmM3PpCINlY8GL3da',
        defaultMessage: 'Save on Travel & Cancellation Protection',
        description: 'Loyalty benefit description for discounted insurance benefit',
      }),
      image: {
        id: '36fmxwdnmim0b53p9x7',
        title: intl.formatMessage({
          id: '4l8szvlwoP_zwb7OeFsK',
          defaultMessage: 'Travel & Cancellation Protection',
          description: 'Alt text for loyalty benefit image showing discounted travel insurance perk',
        }),
      },
      benefitCode: 'discounted_insurance',
      enabled: enabledBenefits.discounted_insurance,
    },
    early_access: undefined,
    esim_package: {
      title: intl.formatMessage({
        id: 'eHaB3yxGo3ORklfl5AHM',
        defaultMessage: 'Stay connected with complimentary roaming data',
        description: 'Loyalty benefit title for eSIM roaming data benefit',
      }),
      description: intl.formatMessage({
        id: 'ttgSNyJ4hp8AIMf2BnzE',
        defaultMessage: 'Enjoy free mobile data on every eligible international trip — with your included data allowance based on membership tier. Get online the moment you land.',
        description: 'Loyalty benefit description for eSIM roaming data benefit',
      }),
      image: {
        id: ESIM_IMAGE_ID,
        title: 'Stay connected with complimentary roaming data',
      },
      benefitCode: 'esim_package',
      enabled: enabledBenefits.esim_package,
    },
    hidden_access: undefined,
  }
}
